import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Download, Loader2, Mail, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useEnvoyerFiche, useFichesSociales, usePdfFiche } from '@/api/social';
import { aVerifier, type FicheSalarie } from './ficheSalarie';

/**
 * Fin de saisie : le PDF est généré et montré, puis on propose de l'envoyer à
 * l'adresse dédiée du service social (pré-remplie, modifiable, retenue).
 */
export default function FinaliserModal({ id, fiche, onClose }: { id: string; fiche: FicheSalarie; onClose: () => void }) {
  const navigate = useNavigate();
  const pdf = usePdfFiche();
  const envoyer = useEnvoyerFiche();
  const { data: liste } = useFichesSociales();
  const [url, setUrl] = useState<string | null>(null);
  const [to, setTo] = useState('');
  const [toTouche, setToTouche] = useState(false);

  // Génération dès l'ouverture.
  useEffect(() => {
    pdf.mutate(id, { onSuccess: (r) => setUrl(URL.createObjectURL(r.blob)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);

  // L'adresse dédiée retenue, tant que le titulaire n'a rien tapé.
  useEffect(() => {
    if (!toTouche && liste?.defaultEmail) setTo(liste.defaultEmail);
  }, [liste?.defaultEmail, toTouche]);

  const telecharger = () => {
    if (!url || !pdf.data) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = pdf.data.fileName;
    a.click();
  };

  const avertissements = aVerifier(fiche);
  const adresseOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim());
  const envoyee = envoyer.isSuccess ? envoyer.data : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-5xl h-[88vh] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white flex items-center justify-between">
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/80">Gestion Sociale</div>
            <div className="text-[17px] font-semibold">Votre fiche est prête</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15" title="Fermer">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 grid md:grid-cols-[1fr_340px]">
          {/* Aperçu */}
          <div className="bg-slate-200 min-h-0">
            {pdf.isPending && (
              <div className="h-full flex flex-col items-center justify-center gap-2 text-[13px] text-slate-600">
                <Loader2 size={22} className="animate-spin text-emerald-600" /> Génération du PDF…
              </div>
            )}
            {pdf.isError && (
              <div className="h-full flex items-center justify-center p-8 text-[13px] text-danger text-center">
                Le PDF n’a pas pu être généré : {(pdf.error as Error).message}
              </div>
            )}
            {url && <iframe src={url} title="Aperçu de la fiche" className="w-full h-full border-0" />}
          </div>

          {/* Actions */}
          <div className="border-l border-slate-200 p-5 flex flex-col gap-5 overflow-y-auto">
            <Button icon={Download} onClick={telecharger} disabled={!url} className="justify-center">
              Télécharger le PDF
            </Button>

            {envoyee ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-[13px] text-emerald-900 space-y-3">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 size={17} className="text-emerald-600" /> Fiche envoyée
                </div>
                <p>
                  Envoyée à <b>{envoyee.sentToEmail}</b>. Vous la retrouverez dans vos fiches, et pourrez la corriger et
                  la renvoyer si besoin.
                </p>
                <Button variant="primary" onClick={() => navigate('/gestion-sociale')} className="w-full justify-center">
                  Retour aux fiches
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[13.5px] font-semibold text-slate-900">
                  <Mail size={15} className="text-emerald-600" /> Envoyer au service social
                </div>
                <div>
                  <label className="label">Adresse e-mail dédiée</label>
                  <input
                    type="email"
                    value={to}
                    onChange={(e) => {
                      setToTouche(true);
                      setTo(e.target.value);
                    }}
                    placeholder="social@votre-cabinet.fr"
                    className="input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {liste?.defaultEmail
                      ? 'Adresse retenue lors du dernier envoi — modifiable.'
                      : 'Elle sera retenue pour les prochains envois.'}
                  </p>
                </div>

                {avertissements.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
                    <div className="flex items-center gap-1.5 font-semibold mb-1">
                      <AlertTriangle size={13} /> Non renseigné (l’envoi reste possible) :
                    </div>
                    <ul className="list-disc pl-5 space-y-0.5">
                      {avertissements.map((a) => (
                        <li key={a}>{a}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {envoyer.isError && <p className="text-[12px] text-danger">{(envoyer.error as Error).message}</p>}

                <button
                  disabled={!adresseOk || envoyer.isPending || !url}
                  onClick={() => envoyer.mutate({ id, to: to.trim() })}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[13.5px] font-semibold text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 shadow-md shadow-emerald-500/25 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {envoyer.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  Envoyer la fiche
                </button>
                <p className="text-[11px] leading-relaxed text-slate-500">
                  Le PDF part en pièce jointe. Il contient des données personnelles (n° de sécurité sociale) : vérifiez
                  l’adresse avant d’envoyer.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
